import React from "react";
import { TGuideRead } from "../../../../../domain/codecs/GuideRead";
import { TGuideReadState, postGuideRead, computeReadState } from "./guideReadsApi";

type TCRMGuidesMarkAsReadProps = {
    product: string;
    slug: string;
    contentHash: string;
    reads: Array<TGuideRead>;
    onRecorded: (row: TGuideRead) => void;
};

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const labelForState = (state: TGuideReadState): { caption: string; cta: string } => {
    switch (state.kind) {
        case "unread":
            return { caption: "", cta: "Mark as read" };
        case "read":
            return { caption: `You read this on ${formatDate(state.read_at)}.`, cta: "Mark as read again" };
        case "stale":
            return { caption: `You read an earlier version on ${formatDate(state.read_at)}. This guide has been updated since.`, cta: "Mark as read again" };
    }
};

export const CRMGuidesMarkAsRead = (props: TCRMGuidesMarkAsReadProps): JSX.Element => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const state = computeReadState(props.reads, props.product, props.slug, props.contentHash);
    const { caption, cta } = labelForState(state);

    const handleClick = async (): Promise<void> => {
        setIsSubmitting(true);
        setError(null);
        try {
            const row = await postGuideRead(props.product, props.slug, props.contentHash);
            props.onRecorded(row);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't record the read — try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="crm-guides-mark-as-read">
            {caption && <p className="crm-guides-mark-as-read__caption">{caption}</p>}
            <button
                type="button"
                className="crm-guides-mark-as-read__button"
                onClick={handleClick}
                disabled={isSubmitting}
            >
                {isSubmitting ? "Recording…" : cta}
            </button>
            {error && <p className="crm-guides-mark-as-read__error">{error}</p>}
        </div>
    );
};
